/**
 * F.6.2A · Wizard QUANTITY_VARIATION — puro componente UI.
 *
 * Raccoglie i dati (voci, nuove quantità, motivazione, note) e invia UN SOLO
 * payload al backend tramite l'endpoint unificato /api/subcontracts/{id}/quantity-variations.
 * Il wizard NON conosce più la sequenza create → emit → apply → snapshot: è tutta
 * responsabilità del backend.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, FileSignature, RefreshCw } from 'lucide-react';
import { applyQuantityVariation, fmtEUR } from '../../lib/contractModificationApi';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const QuantityVariationWizard = ({ open, onOpenChange, subcontract, onCompleted }) => {
  const [step, setStep] = useState(1);
  const [items, setItems] = useState([]);        // items del contratto
  const [selected, setSelected] = useState({});  // itemId → true
  const [newQty, setNewQty] = useState({});      // itemId → number
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !subcontract?.id) return;
    (async () => {
      try {
        // Il backend NON espone /api/subcontracts/{id}/items: gli items sono
        // embedded nella risposta di GET /api/subcontracts/{id}.
        const r = await axios.get(`${API}/api/subcontracts/${subcontract.id}`, auth());
        const list = Array.isArray(r.data?.items) ? r.data.items : [];
        setItems(list.filter(i => i.is_active_blocker !== false));
      } catch (e) {
        toast.error('Impossibile caricare le lavorazioni del contratto');
      }
    })();
    setStep(1);
    setSelected({});
    setNewQty({});
    setReason('');
    setNotes('');
  }, [open, subcontract?.id]);

  const selectedItems = useMemo(() => items.filter(i => selected[i.id]), [items, selected]);

  const deltas = useMemo(() => selectedItems.map(i => {
    const nq = Number(newQty[i.id] ?? i.current_quantity ?? i.quantity);
    const cq = Number(i.current_quantity ?? i.quantity);
    const up = Number(i.unit_price || 0);
    const qtyDelta = nq - cq;
    return {
      id: i.id, code: i.code, description: i.description, unit: i.unit,
      current: cq, next: nq, unit_price: up,
      qty_delta: qtyDelta, amount_delta: Math.round(qtyDelta * up * 100) / 100,
      // F.10A · optimistic concurrency item-level: la UI invia sempre
      // expected_quantity = quantità letta al momento di aprire il wizard.
      expected_quantity: cq,
    };
  }), [selectedItems, newQty]);

  const totalDelta = useMemo(
    () => Math.round(deltas.reduce((s, d) => s + (d.amount_delta || 0), 0) * 100) / 100,
    [deltas],
  );

  const canGoStep2 = selectedItems.length > 0;
  const canGoStep3 = deltas.every(d => Number.isFinite(d.next) && d.next >= 0 && d.qty_delta !== 0);
  const canGoStep4 = reason.trim().length > 0;

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // F.6.2A · UNA SOLA chiamata HTTP: il backend orchestra create+emit+apply+snapshot.
      // F.10A · include expected_quantity per ogni line_variation.
      const result = await applyQuantityVariation(
        subcontract.id,
        deltas.map(d => ({
          subcontract_item_id: d.id,
          new_quantity: d.next,
          expected_quantity: d.expected_quantity,
        })),
        reason.trim(),
        notes.trim() || null,
      );
      const modNumber = result.modification?.modification_number || '';
      toast.success(`Modifica ${modNumber} applicata · Rev.${result.new_contract_revision}`);
      onCompleted?.(result);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      const msg = detail?.message || (typeof detail === 'string' ? detail : null) || e?.message || 'Errore applicazione modifica';
      if (code === 'ROW_REVISION_MISMATCH' || code === 'SUBCONTRACT_ITEM_CONFLICT') {
        // F.10A · concorrenza item-level rilevata: messaggio leggibile + suggerimento reload
        toast.error(
          code === 'SUBCONTRACT_ITEM_CONFLICT'
            ? 'La voce è stata modificata da un altro utente. Ricarica il contratto e riprova.'
            : 'Concorrenza rilevata. Ricarica e riprova.'
        );
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-3xl" data-testid="qv-wizard">
        <DialogHeader>
          <DialogTitle>
            Variazione quantità — Step {step} di 4
            <span className="ml-2 text-xs text-slate-500">
              {subcontract?.subcontract_number} · Rev.{subcontract?.contract_revision}
            </span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="qv-step-1">
            <p className="text-sm text-slate-600">Seleziona le lavorazioni da modificare.</p>
            {items.length === 0 && <p className="text-sm text-amber-700">Nessuna lavorazione disponibile.</p>}
            {items.map(it => (
              <div key={it.id} className="flex items-start gap-2 border-b py-2">
                <Checkbox
                  id={`qv-item-${it.id}`}
                  checked={!!selected[it.id]}
                  onCheckedChange={(v) => setSelected(s => ({ ...s, [it.id]: !!v }))}
                  data-testid={`qv-item-check-${it.id}`}
                />
                <label htmlFor={`qv-item-${it.id}`} className="flex-1 text-sm cursor-pointer">
                  <div className="font-medium">{it.code} · {it.description}</div>
                  <div className="text-xs text-slate-500">
                    {it.unit} · Attuale: <strong>{it.current_quantity ?? it.quantity}</strong> · Prezzo unitario: {fmtEUR(it.unit_price)}
                  </div>
                </label>
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2" data-testid="qv-step-2">
            <p className="text-sm text-slate-600">Inserisci le <strong>nuove quantità</strong>. Prezzo unitario bloccato.</p>
            {selectedItems.map(it => (
              <div key={it.id} className="border rounded p-2">
                <div className="text-sm font-medium">{it.code} · {it.description}</div>
                <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                  <div>Attuale: <strong>{it.current_quantity ?? it.quantity}</strong> {it.unit}</div>
                  <div>Prezzo unitario: <strong>{fmtEUR(it.unit_price)}</strong></div>
                  <div className="flex items-center gap-1">
                    Nuova:
                    <Input
                      type="number" step="any" min="0"
                      className="h-7 w-24"
                      value={newQty[it.id] ?? (it.current_quantity ?? it.quantity)}
                      onChange={e => setNewQty(v => ({ ...v, [it.id]: e.target.value }))}
                      data-testid={`qv-new-qty-${it.id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="qv-step-3">
            <div>
              <Label htmlFor="qv-reason">Motivazione (obbligatoria)</Label>
              <Input id="qv-reason" value={reason} onChange={e => setReason(e.target.value)}
                     placeholder="Es. Aumento superficie da rilievo in cantiere"
                     data-testid="qv-reason" />
            </div>
            <div>
              <Label htmlFor="qv-notes">Note (opzionali)</Label>
              <Textarea id="qv-notes" value={notes} onChange={e => setNotes(e.target.value)}
                        rows={3} data-testid="qv-notes" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="qv-step-4">
            <p className="text-sm font-medium">Riepilogo modifica</p>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100"><tr>
                  <th className="p-2 text-left">Voce</th>
                  <th className="p-2 text-right">Attuale</th>
                  <th className="p-2 text-right">Nuova</th>
                  <th className="p-2 text-right">Δ Qtà</th>
                  <th className="p-2 text-right">Δ Importo</th>
                </tr></thead>
                <tbody>
                  {deltas.map(d => (
                    <tr key={d.id} className="border-t">
                      <td className="p-2">{d.code} · {d.description}</td>
                      <td className="p-2 text-right">{d.current}</td>
                      <td className="p-2 text-right font-medium">{d.next}</td>
                      <td className={`p-2 text-right ${d.qty_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {d.qty_delta > 0 ? '+' : ''}{d.qty_delta}
                      </td>
                      <td className={`p-2 text-right ${d.amount_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {d.amount_delta > 0 ? '+' : ''}{fmtEUR(d.amount_delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr><td colSpan={4} className="p-2 text-right font-semibold">Totale Δ</td>
                    <td className={`p-2 text-right font-semibold ${totalDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="qv-total-delta">
                      {totalDelta > 0 ? '+' : ''}{fmtEUR(totalDelta)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="text-sm text-slate-700">
              <div><strong>Motivazione:</strong> {reason}</div>
              {notes && <div><strong>Note:</strong> {notes}</div>}
            </div>
            <div className="text-xs text-slate-500">
              Confermando la modifica verrà emessa e applicata. Il contratto passerà a <Badge>Rev.{(subcontract?.contract_revision ?? 1) + 1}</Badge>.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="qv-cancel">Annulla</Button>
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={busy}
                    data-testid="qv-prev"><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
          )}
          {step < 4 && (
            <Button onClick={() => setStep(s => s + 1)}
                    disabled={(step === 1 && !canGoStep2) || (step === 2 && !canGoStep3) || (step === 3 && !canGoStep4)}
                    className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                    data-testid="qv-next">Avanti<ChevronRight className="w-4 h-4 ml-1" /></Button>
          )}
          {step === 4 && (
            <Button onClick={submit} disabled={busy}
                    className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                    data-testid="qv-submit">
              {busy ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Applicazione…</>
                    : <><FileSignature className="w-4 h-4 mr-1" /> Emetti · Firma · Applica</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuantityVariationWizard;
